import type {
  ActualizarMarca,
  ListarObservaciones,
  AsientoDeBitacora,
  AnclaDeLaCadena,
  EstadoDeLaCadena,
  ListarBitacora,
  CrearNotaEgreso,
  CrearNotaEvolucion,
  CrearNotaAclaratoria,
  CrearReceta,
  ConsultarDisponibilidad,
  CrearCita,
  CrearColposcopia,
  CrearPaciente,
  CrearUsuario,
  ActualizarUsuario,
  AsignarContrasenaProvisional,
  CambiarContraseña,
  ConfirmarRestablecimiento,
  SolicitarRestablecimiento,
  Medicamento,
  GuardarHistoria,
  DisponibilidadDelDia,
  EstadoCita,
  GuardarConfiguracionAgenda,
  Horarios,
  Ingreso,
  ListarCitas,
  Marca,
  MarcaPublica,
  NotaResumen,
  RecetaResumen,
  SolicitarCita,
  CitaResumen,
  RegistroOrganizacion,
  RespuestaPaginada,
  SesionIniciada,
  UsuarioPublico,
  ComparacionPorOjo,
} from '@exp/contracts';
import type { ActualizarCita, EtiquetaDeCalidad, FaseDeCaptura } from '@exp/contracts';
import type { z } from 'zod';
import type {
  AlergiaDetalle,
  CambiarEstadoEncuentro,
  DerivadoImc,
  DocumentoClinicoDetalle,
  EncuentroDetalle,
  EntradaDeIndiceDocumental,
  Especialidad,
  EstadoDeInformacion,
  MedicacionDetalle,
  ObservacionDetalle,
  NuevaPlantilla,
  EstadoDePlantilla,
  PlantillaVersionDetalle,
  ProblemaDetalle,
  ResumenDeAlergias,
  esquemaActualizarAlergia,
  esquemaActualizarMedicacion,
  esquemaActualizarProblema,
  esquemaCrearDocumentoClinico,
  esquemaCrearEncuentro,
  esquemaRegistrarAlergia,
  esquemaRegistrarMedicacion,
  esquemaRegistrarObservacion,
  esquemaRegistrarProblema,
  EstudioDetalle,
  OrdenDetalle,
  PendienteDeBandeja,
  ReporteDeEstudioDetalle,
  esquemaCrearOrden,
  esquemaGuardarAudiometria,
  esquemaGuardarProcedimiento,
  esquemaRegistrarMuestra,
  esquemaGuardarParametrosEcg,
  esquemaGuardarResultado,
  esquemaRegistrarEstudio,
  AvisoDetalle,
  PreferenciasDeAvisos,
  PreferenciasDeAvisosDetalle,
  InvitacionEmitida,
  InvitacionPortalDetalle,
  VinculoPacienteDetalle,
  esquemaInvitarAPortal,
  Alimentacion,
  EstadoDePlan,
  OcurrenciaDetalle,
  PlanDetalle,
  PlantillaDeAlimentacionDetalle,
  esquemaCrearPlan,
  esquemaGuardarBorradorDePlan,
  CuestionarioDetalle,
  PendienteDeSeguimiento,
  esquemaAsignarCuestionario,
  ConversacionDePaciente,
  ConversacionDetalle,
  ConversacionEnBandeja,
  EstadoDeConversacion,
  esquemaAbrirConversacion,
  ComparticionDetalle,
  TipoDeDocumentoCompartible,
  CredencialDetalle,
  PoliticaDeAcceso,
  AccesoSoporteDetalle,
  VinculoAsistencialDetalle,
  esquemaCredencial,
  esquemaVinculoAsistencial,
} from '@exp/contracts';
import { http } from './http';

/** La API responde `{ datos }`; aqui se desenvuelve una sola vez. */
async function dato<T>(promesa: Promise<{ data: { datos: T } }>): Promise<T> {
  return (await promesa).data.datos;
}

// --- Acceso ---------------------------------------------------------------

export const acceso = {
  ingresar: (datos: Ingreso) => dato<SesionIniciada>(http.post('/auth/ingreso', datos)),
  registrar: (datos: RegistroOrganizacion) =>
    dato<SesionIniciada>(http.post('/auth/registro', datos)),
  /** Responde lo mismo exista o no la cuenta: la pantalla no puede saberlo. */
  solicitarRestablecimiento: (datos: SolicitarRestablecimiento) =>
    dato<{ mensaje: string; correoDisponible: boolean }>(
      http.post('/auth/restablecer/solicitud', datos),
    ),
  enlaceVigente: (token: string) =>
    dato<{ vigente: boolean }>(http.get(`/auth/restablecer/${encodeURIComponent(token)}`)),
  confirmarRestablecimiento: (datos: ConfirmarRestablecimiento) =>
    dato<{ restablecida: boolean }>(http.post('/auth/restablecer/confirmacion', datos)),
};

// --- Consultorio ----------------------------------------------------------

export interface ConsultorioDetalle {
  id: string;
  nombre: string;
  slug: string;
  zonaHoraria: string;
  plan: string;
  marca: Marca & { logoUrl: string | null };
}

export const consultorio = {
  marcaPublica: (slug: string) =>
    dato<MarcaPublica>(http.get(`/organizaciones/publica/${encodeURIComponent(slug)}`)),
  /** Consultorio que sirve este hostname (subdominio o dominio propio verificado, ADR-015). */
  marcaPorHost: () => dato<MarcaPublica>(http.get('/organizaciones/por-host')),
  detalle: () => dato<ConsultorioDetalle>(http.get('/organizacion')),
  actualizarMarca: (cambios: ActualizarMarca) =>
    dato<Marca & { logoUrl: string | null }>(http.patch('/organizacion/marca', cambios)),
  subirLogo: (archivo: File) => {
    const cuerpo = new FormData();
    cuerpo.append('logo', archivo);
    return dato<Marca & { logoUrl: string | null }>(http.post('/organizacion/marca/logo', cuerpo));
  },
};

// --- Usuarios -------------------------------------------------------------

export const usuarios = {
  listar: () => dato<UsuarioPublico[]>(http.get('/usuarios')),
  detalle: (id: string) => dato<UsuarioPublico>(http.get(`/usuarios/${id}`)),
  crear: (datos: CrearUsuario) => dato<UsuarioPublico>(http.post('/usuarios', datos)),
  actualizar: (id: string, cambios: ActualizarUsuario) =>
    dato<UsuarioPublico>(http.patch(`/usuarios/${id}`, cambios)),
  /**
   * El propietario da una clave provisional a otra cuenta (p. ej. si la olvido).
   * El sistema exige cambiarla al entrar: nadie conoce la definitiva de otro.
   */
  asignarContrasenaProvisional: (id: string, datos: AsignarContrasenaProvisional) =>
    dato<UsuarioPublico>(http.post(`/usuarios/${id}/contrasena-provisional`, datos)),
  /** Cambia la contrasena de quien esta dentro. */
  cambiarContrasena: (datos: CambiarContraseña) =>
    dato<{ cambiada: boolean }>(http.post('/usuarios/yo/contrasena', datos)),
};

// --- Agenda ---------------------------------------------------------------

export type { Horarios };

export interface ConfiguracionAgenda extends GuardarConfiguracionAgenda {
  /** Vive en la organización, pero llega junto al horario: quien pinta la
   * agenda necesita las dos cosas y pedirlas aparte sería una petición de más. */
  zonaHoraria: string;
}

export interface MedicoDeAgenda {
  id: string;
  nombre: string;
}

export const agenda = {
  configuracion: () => dato<ConfiguracionAgenda>(http.get('/agenda/configuracion')),
  guardarConfiguracion: (datos: GuardarConfiguracionAgenda) =>
    dato<ConfiguracionAgenda>(http.put('/agenda/configuracion', datos)),
  medicos: () => dato<MedicoDeAgenda[]>(http.get('/agenda/medicos')),
  disponibilidad: (filtros: Partial<ConsultarDisponibilidad> = {}) =>
    dato<DisponibilidadDelDia[]>(http.get('/agenda/disponibilidad', { params: filtros })),
  citas: async (filtros: Partial<ListarCitas> = {}) =>
    (await http.get<RespuestaPaginada<CitaResumen>>('/citas', { params: filtros })).data,
  crearCita: (datos: CrearCita) => dato<CitaResumen>(http.post('/citas', datos)),
  cambiarEstado: (id: string, estado: EstadoCita, motivo = '') =>
    dato<CitaResumen>(http.post(`/citas/${id}/estado`, { estado, motivo })),
  corregirCita: (id: string, cambios: ActualizarCita) =>
    dato<CitaResumen>(http.patch(`/citas/${id}`, cambios)),
};

// --- Portal público del consultorio ---------------------------------------

export interface PortalDelConsultorio {
  marca: MarcaPublica;
  /** Zona del consultorio: el hueco elegido es hora de pared y hay que
   * convertirlo a instante. */
  zonaHoraria: string;
  aceptaSolicitudes: boolean;
  duracionCitaMinutos: number;
  diasReservables: number;
  anticipacionMinimaHoras: number;
  aviso: string;
}

export interface SolicitudAceptada {
  id: string;
  inicio: string;
  fin: string;
  estado: string;
  /** Se envio (o se intento enviar) una copia al correo de la paciente. */
  copiaPorCorreo: boolean;
}

/**
 * El portal se pide SIN sesión: es la página que ve una paciente.
 *
 * El slug va en la ruta, nunca en el cuerpo. Es la misma regla que impone el
 * servidor, repetida aquí para que no haya forma de escribir lo contrario.
 */
export const portal = {
  consultorio: (slug: string) =>
    dato<PortalDelConsultorio>(http.get(`/agenda/publica/${encodeURIComponent(slug)}`)),
  disponibilidad: (slug: string, dias = 14) =>
    dato<DisponibilidadDelDia[]>(
      http.get(`/agenda/publica/${encodeURIComponent(slug)}/disponibilidad`, { params: { dias } }),
    ),
  solicitar: (slug: string, datos: SolicitarCita) =>
    dato<SolicitudAceptada>(
      http.post(`/agenda/publica/${encodeURIComponent(slug)}/solicitudes`, datos),
    ),
};

// --- Pacientes ------------------------------------------------------------

export interface PacienteDetalle {
  id: string;
  expediente: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  nombreCompleto: string;
  sexo: string;
  fechaNacimiento: string;
  edad: number;
  estadoCivil: string | null;
  escolaridad: string | null;
  ocupacion: string;
  telefono: string;
  correo: string;
  domicilio: string;
  contactoEmergencia: { nombre: string; parentesco: string; telefono: string };
  /** Ausente si el rol no lee antecedentes (recepcion, FIND-020). */
  alergias?: string;
  notasAdministrativas: string;
  creadoEn: string;
  modificadoEn: string;
}

export interface PacienteEnLista {
  id: string;
  expediente: string;
  nombreCompleto: string;
  sexo: string;
  fechaNacimiento: string;
  edad: number;
  telefono: string;
  creadoEn: string;
}

export const pacientes = {
  buscar: async (texto: string, pagina = 1) =>
    (
      await http.get<RespuestaPaginada<PacienteEnLista>>('/pacientes', {
        params: { texto: texto || undefined, pagina },
      })
    ).data,
  detalle: (id: string) => dato<PacienteDetalle>(http.get(`/pacientes/${id}`)),
  // `alergias` es opcional: recepcion no la manda (FIND-020) y la API la toma vacia.
  crear: (datos: Omit<CrearPaciente, 'alergias'> & { alergias?: string }) =>
    dato<PacienteDetalle>(http.post('/pacientes', datos)),
  actualizar: (id: string, cambios: Partial<CrearPaciente>) =>
    dato<PacienteDetalle>(http.patch(`/pacientes/${id}`, cambios)),
  /**
   * Baja logica: el expediente se conserva, deja de aparecer en la busqueda.
   *
   * Responde 204 sin cuerpo, asi que no pasa por `dato`.
   */
  darDeBaja: async (id: string, motivo: string): Promise<void> => {
    await http.delete(`/pacientes/${id}`, { data: { motivo } });
  },
};

// --- Historia clínica -----------------------------------------------------

export interface HistoriaDetalle extends Omit<GuardarHistoria, 'motivoEdicion'> {
  id: string;
  pacienteId: string;
  imc: number | null;
  clasificacionImc: string;
  version: number;
  motivoUltimaEdicion: string;
  firma: SelloFirma | null;
  creadoEn: string;
  modificadoEn: string;
}

export const historia = {
  obtener: (pacienteId: string) =>
    dato<HistoriaDetalle>(http.get(`/pacientes/${pacienteId}/historia`)),
  guardar: (pacienteId: string, datos: GuardarHistoria) =>
    dato<HistoriaDetalle>(http.put(`/pacientes/${pacienteId}/historia`, datos)),
  verificacion: (pacienteId: string) =>
    dato<VerificacionFirma>(http.get(`/pacientes/${pacienteId}/historia/verificacion`)),
  versiones: (pacienteId: string) =>
    dato<VersionDeDocumento[]>(http.get(`/pacientes/${pacienteId}/historia/versiones`)),
};

// --- Bitácora y exportación -----------------------------------------------

export const bitacora = {
  listar: async (filtros: Partial<ListarBitacora> = {}) =>
    (await http.get<RespuestaPaginada<AsientoDeBitacora>>('/auditoria', { params: filtros })).data,
  verificacion: () => dato<EstadoDeLaCadena>(http.get('/auditoria/verificacion')),
  ancla: () => dato<AnclaDeLaCadena | null>(http.get('/auditoria/ancla')),
};

export const expediente = {
  /**
   * Descarga el expediente completo.
   *
   * Se pide con el token y se guarda desde memoria: el endpoint es autenticado,
   * así que un enlace normal del navegador no serviría, y poner el token en la
   * URL lo dejaría en el historial. El nombre del archivo sale de la cabecera
   * que manda el servidor.
   */
  exportar: async (pacienteId: string, expedienteNumero: string) => {
    const respuesta = await http.get<Blob>(`/pacientes/${pacienteId}/exportacion`, {
      responseType: 'blob',
    });

    const enlace = URL.createObjectURL(respuesta.data);
    const ancla = document.createElement('a');
    ancla.href = enlace;
    ancla.download = `expediente-${expedienteNumero}.zip`;
    document.body.appendChild(ancla);
    ancla.click();
    ancla.remove();
    URL.revokeObjectURL(enlace);
  },
};

// --- Recetas y notas ------------------------------------------------------

/**
 * Abre un documento imprimible en una pestaña nueva.
 *
 * Se pide con el token y se abre desde memoria, igual que el informe de
 * colposcopía: una navegación normal del navegador no lleva cabecera de
 * autorización, y poner el token en la URL lo dejaría en el historial y en los
 * registros del servidor.
 */
async function abrirImpresion(ruta: string): Promise<boolean> {
  const { data } = await http.get<string>(ruta, { responseType: 'text' });
  return abrirDocumento(data);
}

/** Abre un documento autocontenido en otra pestana. Falso si el navegador la bloqueo. */
function abrirDocumento(data: string): boolean {
  const enlace = URL.createObjectURL(new Blob([data], { type: 'text/html;charset=utf-8' }));
  // Sin la bandera 'noopener': con ella el estandar obliga a devolver `null`
  // aunque la ventana se abra, y no habria forma de saber si el navegador la
  // bloqueo (y el blob se revocaria antes de cargar). Se corta `opener` a mano,
  // que es la misma proteccion.
  const ventana = window.open(enlace, '_blank');
  if (ventana) ventana.opener = null;
  setTimeout(() => URL.revokeObjectURL(enlace), ventana ? 60_000 : 0);
  return Boolean(ventana);
}

export interface RecetaDetalle {
  id: string;
  folio: string;
  pacienteId: string;
  colposcopiaId: string | null;
  fecha: string;
  diagnostico: string;
  medicamentos: Medicamento[];
  indicacionesGenerales: string;
  alergiasConfirmadas: string;
  version: number;
  motivoUltimaEdicion: string;
  cancelada: boolean;
  motivoCancelacion: string;
  firma: SelloFirma | null;
  creadoEn: string;
  modificadoEn: string;
}

export const recetas = {
  listar: async (pacienteId: string) =>
    (await http.get<RespuestaPaginada<RecetaResumen>>('/recetas', { params: { pacienteId } })).data,
  detalle: (id: string) => dato<RecetaDetalle>(http.get(`/recetas/${id}`)),
  crear: (datos: CrearReceta) => dato<{ id: string; folio: string }>(http.post('/recetas', datos)),
  /**
   * Corregir una receta es emitirla de nuevo: sube de version, exige motivo y
   * vuelve a firmar. No es una edicion silenciosa, y por eso el motivo es
   * obligatorio en la firma del metodo.
   */
  corregir: (id: string, cambios: Record<string, unknown>, motivo: string) =>
    dato<RecetaDetalle>(http.patch(`/recetas/${id}`, { ...cambios, motivoEdicion: motivo })),
  cancelar: (id: string, motivo: string) =>
    dato<{ id: string }>(http.post(`/recetas/${id}/cancelacion`, { motivo })),
  verificacion: (id: string) => dato<VerificacionFirma>(http.get(`/recetas/${id}/verificacion`)),
  versiones: (id: string) => dato<VersionDeDocumento[]>(http.get(`/recetas/${id}/versiones`)),
  abrirImpresion: (id: string) => abrirImpresion(`/recetas/${id}/impresion`),
};

/**
 * Detalle de una nota. Los campos que llegan dependen de `tipo`, igual que en
 * el serializador del backend: una nota de egreso no trae los de una evolucion.
 */
export interface NotaDetalle {
  id: string;
  folio: string;
  tipo: 'evolucion' | 'egreso' | 'aclaratoria';
  pacienteId: string;
  fecha: string;
  version: number;
  motivoUltimaEdicion: string;
  firma: SelloFirma | null;
  creadoEn: string;
  modificadoEn: string;
  // Evolución
  padecimiento?: string;
  exploracion?: string;
  resultadosEstudios?: string;
  diagnosticos?: string;
  tratamiento?: string;
  pronostico?: string;
  colposcopiaId?: string | null;
  // Egreso
  fechaInicioAtencion?: string;
  motivoEgreso?: string;
  diagnosticosFinales?: string;
  resumenEvolucion?: string;
  manejo?: string;
  problemasPendientes?: string;
  planDeManejo?: string;
  recomendacionesVigilancia?: string;
  // Aclaratoria
  aclara?: { tipo: string; id: string } | null;
  loAsentado?: string;
  aclaracion?: string;
  motivo?: string;
}

export const notas = {
  listar: async (pacienteId: string) =>
    (await http.get<RespuestaPaginada<NotaResumen>>('/notas', { params: { pacienteId } })).data,
  detalle: (id: string) => dato<NotaDetalle>(http.get(`/notas/${id}`)),
  crearEvolucion: (datos: CrearNotaEvolucion) =>
    dato<{ id: string; folio: string }>(http.post('/notas/evolucion', datos)),
  crearEgreso: (datos: CrearNotaEgreso) =>
    dato<{ id: string; folio: string }>(http.post('/notas/egreso', datos)),
  crearAclaracion: (datos: CrearNotaAclaratoria) =>
    dato<{ id: string; folio: string }>(http.post('/notas/aclaracion', datos)),
  corregir: (id: string, cambios: Record<string, unknown>, motivo: string) =>
    dato<NotaDetalle>(http.patch(`/notas/${id}`, { ...cambios, motivoEdicion: motivo })),
  verificacion: (id: string) => dato<VerificacionFirma>(http.get(`/notas/${id}/verificacion`)),
  versiones: (id: string) => dato<VersionDeDocumento[]>(http.get(`/notas/${id}/versiones`)),
  abrirImpresion: (id: string) => abrirImpresion(`/notas/${id}/impresion`),
};

/**
 * Aclaraciones que apuntan a un documento.
 *
 * Es la pieza que hace VISIBLE la correccion: sin esto, una nota de aclaracion
 * existia en la base y no habia forma de llegar a ella desde el documento que
 * aclara, que es justo donde alguien la buscaria.
 */
export const documentos = {
  aclaraciones: (documentoId: string) =>
    dato<NotaResumen[]>(http.get(`/documentos/${documentoId}/aclaraciones`)),
};

// --- Colposcopías ---------------------------------------------------------

export interface SelloFirma {
  medicoNombre: string;
  cedulaProfesional: string;
  especialidad: string;
  unidadMedica: string;
  fechaHoraFirma: string;
  hashContenido: string;
  idLlavePublica?: string;
  versionFirma?: string;
}

/** Una medicion sobre una imagen; los valores los calcula el servidor (ADR-017). */
export interface MedicionEnImagen {
  tipo: 'distancia' | 'area';
  /** Normalizados 0..1. */
  puntos: { x: number; y: number }[];
  nota: string;
  valorPx?: number;
  valorMm?: number | null;
  modo?: 'calibrado' | 'nominal';
  posicionHoraria?: number | null;
  cuadrante?: 'Q1' | 'Q2' | 'Q3' | 'Q4' | null;
}

/** Datos de la captura del colposcopio (ADR-017). Ausentes en imagenes subidas antes. */
export interface DatosDeCaptura {
  origen?: 'captura' | 'subida' | null;
  fase?: FaseDeCaptura | null;
  calidad?: { puntuacion: number; etiqueta: EtiquetaDeCalidad } | null;
  /** Normalizado: cx/ancho, cy/alto, r/min(ancho, alto). */
  cervix?: { cx: number; cy: number; r: number } | null;
  calibracionId?: string | null;
  mediciones?: MedicionEnImagen[];
}

export interface ImagenEnEstudio extends DatosDeCaptura {
  archivoId: string;
  etiqueta: string;
  orden: number;
  url: string;
  urlMiniatura: string;
  sha256?: string | null;
  ancho?: number | null;
  alto?: number | null;
  escala?: { modo: 'calibrado' | 'nominal'; pxPorMm: number; calibracionId: string | null } | null;
}

export interface CalibracionPublica {
  id: string;
  etiquetaDispositivo: string;
  ancho: number;
  alto: number;
  referenciaPx: number;
  referenciaMm: number;
  pxPorMm: number;
  vigente: boolean;
  creadoEn: string;
}

export interface ColposcopiaDetalle {
  id: string;
  folio: string;
  pacienteId: string;
  fecha: string;
  motivo: string;
  evaluacion: Record<string, string>;
  criterios: Record<string, string[]>;
  accesorios: Record<string, string>;
  miscelaneos: string[];
  hallazgos: string;
  diagnostico: string;
  gradoMaximo: 'ninguno' | 'menores' | 'intermedios' | 'mayores';
  observaciones: string;
  plan: string;
  tomaBiopsia: boolean;
  sitiosBiopsia: string;
  imagenes: ImagenEnEstudio[];
  version: number;
  motivoUltimaEdicion: string;
  cancelada: boolean;
  motivoCancelacion: string;
  firma: SelloFirma | null;
  creadoEn: string;
  modificadoEn: string;
}

export interface ColposcopiaEnLista {
  id: string;
  folio: string;
  pacienteId: string;
  fecha: string;
  diagnostico: string;
  gradoMaximo: 'ninguno' | 'menores' | 'intermedios' | 'mayores';
  tomaBiopsia: boolean;
  imagenes: number;
  version: number;
  cancelada: boolean;
  firmadaPor: string;
}

export interface VerificacionFirma {
  valida: boolean;
  contenidoIntacto: boolean;
  firmaIntacta: boolean;
  llaveDistinta: boolean;
  /**
   * Solo en documentos que se pueden cancelar (estudio y receta). El sello
   * original se verifica aparte de la cancelacion: un documento cancelado con
   * su sello original intacto es un documento integro, no uno alterado.
   */
  selloOriginalIntacto?: boolean;
  cancelada?: boolean;
  /** `null` si no hay ninguna huella de cancelacion. */
  cancelacion?: EstadoCancelacion | null;
}

export interface EstadoCancelacion {
  motivo: string;
  fechaHora: string | null;
  usuarioNombre: string;
  /** Lleva sello propio de cancelacion (las anteriores a FIND-002 no). */
  sellada: boolean;
  /** Si el sello de cancelacion cuadra; `null` si no hay sello. */
  selloIntacto: boolean | null;
  /** La bitacora registra la cancelacion con el mismo motivo. */
  enBitacora: boolean;
}

/**
 * Version anterior de un documento firmado, conservada al corregirlo. Es de
 * solo lectura: la conserva el servidor y se vuelve a verificar al leerla.
 */
export interface VersionDeDocumento {
  id: string;
  entidad: string;
  documentoId: string;
  version: number;
  contenido: Record<string, unknown>;
  firma: SelloFirma | null;
  verificacion: VerificacionFirma;
  selloIntactoAlConservar: boolean;
  /** Hay asiento en la bitacora de la correccion que la reemplazo. */
  reemplazoEnBitacora: boolean;
  motivoReemplazo: string;
  reemplazadaEn: string;
  reemplazadaPor: { id: string | null; nombre: string };
}

export interface ImagenSubida {
  archivoId: string;
  ancho: number | null;
  alto: number | null;
  sha256?: string | null;
  url: string;
  urlMiniatura: string;
}

export const colposcopias = {
  listar: async (pacienteId?: string, pagina = 1) =>
    (
      await http.get<RespuestaPaginada<ColposcopiaEnLista>>('/colposcopias', {
        params: { pacienteId, pagina },
      })
    ).data,
  detalle: (id: string) => dato<ColposcopiaDetalle>(http.get(`/colposcopias/${id}`)),
  crear: (datos: CrearColposcopia) => dato<ColposcopiaDetalle>(http.post('/colposcopias', datos)),
  corregir: (id: string, cambios: Record<string, unknown>) =>
    dato<ColposcopiaDetalle>(http.patch(`/colposcopias/${id}`, cambios)),
  cancelar: (id: string, motivo: string) =>
    dato<ColposcopiaDetalle>(http.post(`/colposcopias/${id}/cancelacion`, { motivo })),
  verificacion: (id: string) =>
    dato<VerificacionFirma>(http.get(`/colposcopias/${id}/verificacion`)),
  versiones: (id: string) => dato<VersionDeDocumento[]>(http.get(`/colposcopias/${id}/versiones`)),
  calibracionVigente: (huella: string, ancho: number, alto: number) =>
    dato<CalibracionPublica | null>(
      http.get('/colposcopias/calibraciones', { params: { huella, ancho, alto } }),
    ),
  calibrar: (datos: {
    huellaDispositivo: string;
    etiquetaDispositivo: string;
    ancho: number;
    alto: number;
    referenciaPx: number;
    referenciaMm: number;
  }) => dato<CalibracionPublica>(http.post('/colposcopias/calibraciones', datos)),
  retirarCalibracion: (id: string) =>
    dato<CalibracionPublica>(http.post(`/colposcopias/calibraciones/${id}/retiro`)),
  subirImagenes: (archivos: File[], pacienteId: string) => {
    const cuerpo = new FormData();
    for (const archivo of archivos) cuerpo.append('imagenes', archivo);
    cuerpo.append('pacienteId', pacienteId);
    return dato<ImagenSubida[]>(http.post('/colposcopias/imagenes', cuerpo));
  },
  /**
   * Abre el informe en una pestana nueva.
   *
   * Se pide con el token y se abre desde memoria, en lugar de enlazar la ruta:
   * una navegacion normal del navegador no lleva cabecera de autorizacion, y
   * poner el token en la URL lo dejaria en el historial y en los registros del
   * servidor.
   */
  abrirInforme: async (id: string) => {
    const { data } = await http.get<string>(`/colposcopias/${id}/informe`, {
      responseType: 'text',
    });
    return abrirDocumento(data);
  },
};

// --- Nucleo clinico (F2.2–F2.7) ----------------------------------------------
//
// Todo pasa por el servidor: nada clinico se guarda en el navegador (plan
// §15.3). Los cambios llevan la version que se leyo; un 409 significa que otra
// persona cambio el registro y hay que volver a cargarlo.

export const encuentros = {
  listar: async (pacienteId: string) =>
    (
      await http.get<RespuestaPaginada<EncuentroDetalle>>('/encuentros', {
        params: { pacienteId, porPagina: 50 },
      })
    ).data,
  crear: (datos: z.input<typeof esquemaCrearEncuentro>) =>
    dato<EncuentroDetalle>(http.post('/encuentros', datos)),
  cambiarEstado: (id: string, cambio: CambiarEstadoEncuentro) =>
    dato<EncuentroDetalle>(http.post(`/encuentros/${id}/estado`, cambio)),
};

export const observaciones = {
  listar: async (
    pacienteId: string,
    filtros: Partial<Omit<ListarObservaciones, 'pacienteId'>> = {},
    signal?: AbortSignal,
  ) =>
    (
      await http.get<RespuestaPaginada<ObservacionDetalle>>('/observaciones', {
        params: { pacienteId, porPagina: 200, ...filtros },
        ...(signal ? { signal } : {}),
      })
    ).data,
  registrar: (datos: z.input<typeof esquemaRegistrarObservacion>) =>
    dato<ObservacionDetalle>(http.post('/observaciones', datos)),
  imc: (pacienteId: string) => dato<DerivadoImc>(http.get(`/pacientes/${pacienteId}/imc`)),
};

export const antecedentes = {
  problemas: (pacienteId: string) =>
    dato<ProblemaDetalle[]>(http.get(`/pacientes/${pacienteId}/problemas`)),
  registrarProblema: (datos: z.input<typeof esquemaRegistrarProblema>) =>
    dato<ProblemaDetalle>(http.post('/problemas', datos)),
  actualizarProblema: (id: string, cambios: z.input<typeof esquemaActualizarProblema>) =>
    dato<ProblemaDetalle>(http.patch(`/problemas/${id}`, cambios)),
  alergias: (pacienteId: string) =>
    dato<ResumenDeAlergias>(http.get(`/pacientes/${pacienteId}/alergias`)),
  registrarAlergia: (datos: z.input<typeof esquemaRegistrarAlergia>) =>
    dato<AlergiaDetalle>(http.post('/alergias', datos)),
  actualizarAlergia: (id: string, cambios: z.input<typeof esquemaActualizarAlergia>) =>
    dato<AlergiaDetalle>(http.patch(`/alergias/${id}`, cambios)),
  declararAlergias: (pacienteId: string, estado: EstadoDeInformacion) =>
    dato<{ estado: EstadoDeInformacion }>(
      http.put(`/pacientes/${pacienteId}/alergias/declaracion`, { estado }),
    ),
  medicacion: (pacienteId: string) =>
    dato<MedicacionDetalle[]>(http.get(`/pacientes/${pacienteId}/medicacion`)),
  registrarMedicacion: (datos: z.input<typeof esquemaRegistrarMedicacion>) =>
    dato<MedicacionDetalle>(http.post('/medicacion', datos)),
  actualizarMedicacion: (id: string, cambios: z.input<typeof esquemaActualizarMedicacion>) =>
    dato<MedicacionDetalle>(http.patch(`/medicacion/${id}`, cambios)),
};

export interface EspecialidadDelConsultorio {
  especialidad: Especialidad;
  nombre: string;
  contratada: boolean;
  paquete: { version: string; estado: string; plantillas: string[] };
}

export const plantillas = {
  especialidades: () => dato<EspecialidadDelConsultorio[]>(http.get('/especialidades')),
  listar: () => dato<PlantillaVersionDetalle[]>(http.get('/plantillas')),
  obtener: (id: string) => dato<PlantillaVersionDetalle>(http.get(`/plantillas/${id}`)),
  // Administracion (F2.10): solo el propietario. El servidor valida la
  // definicion completa (tipos cerrados, limites, condiciones) y responde 400.
  crear: (definicion: NuevaPlantilla) =>
    dato<PlantillaVersionDetalle>(http.post('/plantillas', definicion)),
  importar: (referencia: string) =>
    dato<PlantillaVersionDetalle>(http.post('/plantillas/importacion', { referencia })),
  actualizar: (id: string, definicion: NuevaPlantilla) =>
    dato<PlantillaVersionDetalle>(http.patch(`/plantillas/${id}`, definicion)),
  clonar: (id: string) => dato<PlantillaVersionDetalle>(http.post(`/plantillas/${id}/clon`)),
  cambiarEstado: (id: string, estado: EstadoDePlantilla, motivo?: string) =>
    dato<PlantillaVersionDetalle>(
      http.post(`/plantillas/${id}/estado`, { estado, ...(motivo ? { motivo } : {}) }),
    ),
};

export const documentosClinicos = {
  listar: (pacienteId: string) =>
    dato<DocumentoClinicoDetalle[]>(http.get(`/pacientes/${pacienteId}/documentos-clinicos`)),
  obtener: (id: string) => dato<DocumentoClinicoDetalle>(http.get(`/documentos-clinicos/${id}`)),
  /** Firma y, aparte, si la version de plantilla sigue siendo la capturada. */
  verificacion: (id: string) =>
    dato<VerificacionFirma & { plantillaIntacta: boolean }>(
      http.get(`/documentos-clinicos/${id}/verificacion`),
    ),
  crear: (datos: z.input<typeof esquemaCrearDocumentoClinico>) =>
    dato<DocumentoClinicoDetalle>(http.post('/documentos-clinicos', datos)),
  /** Copiar una visita (F6.1): borrador nuevo en otro encuentro de la paciente. */
  copiar: (id: string, encuentroId: string, incluirMediciones: boolean) =>
    dato<DocumentoClinicoDetalle & { omitidos: string[] }>(
      http.post(`/documentos-clinicos/${id}/copia`, { encuentroId, incluirMediciones }),
    ),
  actualizar: (id: string, versionEsperada: number, respuestas: Record<string, unknown>) =>
    dato<DocumentoClinicoDetalle>(
      http.patch(`/documentos-clinicos/${id}`, { versionEsperada, respuestas }),
    ),
  firmar: (id: string, versionEsperada: number) =>
    dato<DocumentoClinicoDetalle>(
      http.post(`/documentos-clinicos/${id}/firma`, { versionEsperada }),
    ),
  /** Indice comun de todos los documentos de la paciente (F2.7). */
  indice: (pacienteId: string) =>
    dato<EntradaDeIndiceDocumental[]>(http.get(`/pacientes/${pacienteId}/documentos`)),
};

// --- Archivos de la paciente y estudios (F3.2, F3.4, F3.5) ------------------

export interface ArchivoDePaciente {
  archivoId: string;
  nombreOriginal: string;
  tipoMime: string;
  tamano: number;
  sha256: string;
  estado: 'cuarentena' | 'disponible' | 'rechazado';
  motivoRechazo: string;
  creadoEn: string;
}

export const archivosDePaciente = {
  listar: (pacienteId: string) =>
    dato<ArchivoDePaciente[]>(http.get(`/pacientes/${pacienteId}/archivos`)),
  /** Entra en cuarentena (202): no esta disponible hasta que el worker lo analiza. */
  subir: (pacienteId: string, archivo: File) => {
    const formulario = new FormData();
    formulario.append('archivo', archivo);
    return dato<{ archivoId: string; estado: ArchivoDePaciente['estado']; sha256: string }>(
      http.post(`/pacientes/${pacienteId}/archivos`, formulario),
    );
  },
};

export const oftalmologia = {
  comparacion: (pacienteId: string) =>
    dato<ComparacionPorOjo[]>(http.get(`/pacientes/${pacienteId}/oftalmologia/comparacion`)),
};

export const estudios = {
  dePaciente: (pacienteId: string) =>
    dato<{ ordenes: OrdenDetalle[]; estudios: EstudioDetalle[] }>(
      http.get(`/pacientes/${pacienteId}/estudios`),
    ),
  obtener: (id: string) => dato<EstudioDetalle>(http.get(`/estudios/${id}`)),
  registrar: (datos: z.input<typeof esquemaRegistrarEstudio>) =>
    dato<EstudioDetalle>(http.post('/estudios', datos)),
  guardarResultado: (id: string, datos: z.input<typeof esquemaGuardarResultado>) =>
    dato<EstudioDetalle>(http.put(`/estudios/${id}/resultado`, datos)),
  guardarAudiometria: (id: string, datos: z.input<typeof esquemaGuardarAudiometria>) =>
    dato<EstudioDetalle>(http.put(`/estudios/${id}/audiometria`, datos)),
  guardarEcg: (id: string, datos: z.input<typeof esquemaGuardarParametrosEcg>) =>
    dato<EstudioDetalle>(http.put(`/estudios/${id}/ecg`, datos)),
  confirmarCorrespondencia: (id: string, versionEsperada: number) =>
    dato<EstudioDetalle>(http.post(`/estudios/${id}/correspondencia`, { versionEsperada })),
  reportes: (id: string) => dato<ReporteDeEstudioDetalle[]>(http.get(`/estudios/${id}/reportes`)),
  firmarReporte: (id: string, versionEsperada: number) =>
    dato<ReporteDeEstudioDetalle>(http.post(`/estudios/${id}/reportes`, { versionEsperada })),
  publicar: (id: string, versionEsperada: number) =>
    dato<EstudioDetalle>(http.post(`/estudios/${id}/publicacion`, { versionEsperada })),
  // F6.3: ordenes, muestras, procedimiento y bandeja.
  crearOrden: (datos: z.input<typeof esquemaCrearOrden>) =>
    dato<OrdenDetalle>(http.post('/estudios/ordenes', datos)),
  registrarMuestra: (ordenId: string, datos: z.input<typeof esquemaRegistrarMuestra>) =>
    dato<OrdenDetalle>(http.post(`/estudios/ordenes/${ordenId}/muestras`, datos)),
  guardarProcedimiento: (id: string, datos: z.input<typeof esquemaGuardarProcedimiento>) =>
    dato<EstudioDetalle>(http.put(`/estudios/${id}/procedimiento`, datos)),
  bandeja: () => dato<PendienteDeBandeja[]>(http.get('/estudios/bandeja')),
};

// --- Avisos a la paciente (F4.1) ---------------------------------------------

export const avisosDePaciente = {
  obtener: (pacienteId: string) =>
    dato<{ preferencias: PreferenciasDeAvisosDetalle; avisos: AvisoDetalle[] }>(
      http.get(`/pacientes/${pacienteId}/avisos`),
    ),
  guardar: (pacienteId: string, datos: PreferenciasDeAvisos) =>
    dato<PreferenciasDeAvisosDetalle>(
      http.put(`/pacientes/${pacienteId}/avisos/preferencias`, datos),
    ),
};

// --- Acceso de la paciente a su portal (F4.2) --------------------------------

export const portalDePaciente = {
  obtener: (pacienteId: string) =>
    dato<{ invitaciones: InvitacionPortalDetalle[]; vinculos: VinculoPacienteDetalle[] }>(
      http.get(`/pacientes/${pacienteId}/portal`),
    ),
  invitar: (pacienteId: string, datos: z.input<typeof esquemaInvitarAPortal>) =>
    dato<InvitacionEmitida>(http.post(`/pacientes/${pacienteId}/portal/invitaciones`, datos)),
  revocar: (pacienteId: string, vinculoId: string, motivo: string) =>
    dato<VinculoPacienteDetalle>(
      http.post(`/pacientes/${pacienteId}/portal/vinculos/${vinculoId}/revocacion`, { motivo }),
    ),
  renovar: (pacienteId: string, vinculoId: string, vigenteHasta: string, motivo: string) =>
    dato<VinculoPacienteDetalle>(
      http.patch(`/pacientes/${pacienteId}/portal/vinculos/${vinculoId}/representacion`, {
        vigenteHasta,
        motivo,
      }),
    ),
};

// --- Planes de atención y alimentación (F4.5, F4.6) ---------------------------

export const planes = {
  dePaciente: (pacienteId: string) =>
    dato<PlanDetalle[]>(http.get(`/pacientes/${pacienteId}/planes`)),
  obtener: (id: string) => dato<PlanDetalle>(http.get(`/planes/${id}`)),
  crear: (datos: z.input<typeof esquemaCrearPlan>) =>
    dato<PlanDetalle>(http.post('/planes', datos)),
  guardarBorrador: (id: string, datos: z.input<typeof esquemaGuardarBorradorDePlan>) =>
    dato<PlanDetalle>(http.put(`/planes/${id}/borrador`, datos)),
  transicion: (id: string, versionEsperada: number, estado: EstadoDePlan, motivo?: string) =>
    dato<PlanDetalle>(
      http.post(`/planes/${id}/transicion`, {
        versionEsperada,
        estado,
        ...(motivo ? { motivo } : {}),
      }),
    ),
  publicar: (id: string, versionEsperada: number, entraEnVigor?: string) =>
    dato<PlanDetalle>(
      http.post(`/planes/${id}/publicacion`, {
        versionEsperada,
        ...(entraEnVigor ? { entraEnVigor } : {}),
      }),
    ),
  ocurrencias: (id: string) => dato<OcurrenciaDetalle[]>(http.get(`/planes/${id}/ocurrencias`)),
  plantillas: () =>
    dato<PlantillaDeAlimentacionDetalle[]>(http.get('/planes/plantillas-de-alimentacion')),
  guardarPlantilla: (nombre: string, alimentacion: Alimentacion) =>
    dato<PlantillaDeAlimentacionDetalle>(
      http.post('/planes/plantillas-de-alimentacion', { nombre, alimentacion }),
    ),
};

// --- Seguimiento (F4.7) --------------------------------------------------------

export const seguimiento = {
  bandeja: () => dato<PendienteDeSeguimiento[]>(http.get('/seguimiento/bandeja')),
  revisarMedicion: (id: string, estado: 'revisada' | 'descartada', motivo?: string) =>
    dato<ObservacionDetalle>(
      http.post(`/observaciones/${id}/revision`, { estado, ...(motivo ? { motivo } : {}) }),
    ),
  cuestionarios: (pacienteId: string) =>
    dato<CuestionarioDetalle[]>(http.get(`/pacientes/${pacienteId}/cuestionarios`)),
  asignar: (datos: z.input<typeof esquemaAsignarCuestionario>) =>
    dato<CuestionarioDetalle>(http.post('/cuestionarios', datos)),
  revisarCuestionario: (id: string, nota: string) =>
    dato<CuestionarioDetalle>(http.post(`/cuestionarios/${id}/revision`, { nota })),
};

// --- Mensajeria (F4.8) ---------------------------------------------------------

export const mensajes = {
  bandeja: (filtros: { estado?: EstadoDeConversacion; mias?: boolean } = {}) =>
    dato<ConversacionEnBandeja[]>(
      http.get('/mensajes/bandeja', {
        params: {
          ...(filtros.estado ? { estado: filtros.estado } : {}),
          ...(filtros.mias ? { mias: '1' } : {}),
        },
      }),
    ),
  detalle: (id: string) => dato<ConversacionDetalle>(http.get(`/mensajes/${id}`)),
  dePaciente: (pacienteId: string) =>
    dato<ConversacionDePaciente[]>(http.get(`/pacientes/${pacienteId}/conversaciones`)),
  abrir: (pacienteId: string, datos: z.input<typeof esquemaAbrirConversacion>) =>
    dato<ConversacionDetalle>(http.post(`/pacientes/${pacienteId}/conversaciones`, datos)),
  responder: (id: string, texto: string) =>
    dato<ConversacionDetalle>(http.post(`/mensajes/${id}/mensajes`, { texto, adjuntoIds: [] })),
  asignar: (id: string, responsableId: string, suplenteId?: string) =>
    dato<ConversacionDetalle>(
      http.post(`/mensajes/${id}/asignacion`, {
        responsableId,
        ...(suplenteId ? { suplenteId } : {}),
      }),
    ),
  cambiarEstado: (id: string, estado: EstadoDeConversacion, motivo?: string) =>
    dato<ConversacionDetalle>(
      http.post(`/mensajes/${id}/estado`, { estado, ...(motivo ? { motivo } : {}) }),
    ),
};

// --- Documentos compartidos con la paciente (F4.9) -----------------------------

export const documentosCompartidos = {
  dePaciente: (pacienteId: string) =>
    dato<ComparticionDetalle[]>(http.get(`/pacientes/${pacienteId}/documentos-compartidos`)),
  compartir: (pacienteId: string, tipo: TipoDeDocumentoCompartible, documentoId: string) =>
    dato<ComparticionDetalle>(
      http.post(`/pacientes/${pacienteId}/documentos-compartidos`, { tipo, documentoId }),
    ),
  retirar: (id: string, motivo: string) =>
    dato<ComparticionDetalle>(http.post(`/documentos-compartidos/${id}/retiro`, { motivo })),
};

// --- Equipo y acceso (F5.5) --------------------------------------------------

export interface SucursalDetalle {
  _id: string;
  nombre: string;
  direccion: string;
  activa: boolean;
}
export interface EquipoDetalle {
  _id: string;
  nombre: string;
  sucursalId: string | null;
  miembros: string[];
  activo: boolean;
}

export const equipoYAcceso = {
  politica: () => dato<{ politica: PoliticaDeAcceso }>(http.get('/organizacion/politica-acceso')),
  cambiarPolitica: (politica: PoliticaDeAcceso, motivo: string) =>
    dato<{ vinculosCreados: number }>(
      http.put('/organizacion/politica-acceso', { politica, motivo }),
    ),
  sucursales: () => dato<SucursalDetalle[]>(http.get('/sucursales')),
  crearSucursal: (nombre: string, direccion: string) =>
    dato<SucursalDetalle>(http.post('/sucursales', { nombre, direccion })),
  equipos: () => dato<EquipoDetalle[]>(http.get('/equipos')),
  guardarEquipo: (
    datos: { nombre: string; sucursalId: string | null; miembros: string[] },
    id?: string,
  ) => dato<EquipoDetalle>(id ? http.put(`/equipos/${id}`, datos) : http.post('/equipos', datos)),
  vinculos: (pacienteId: string) =>
    dato<VinculoAsistencialDetalle[]>(http.get(`/pacientes/${pacienteId}/vinculos`)),
  vincular: (pacienteId: string, datos: z.input<typeof esquemaVinculoAsistencial>) =>
    dato<VinculoAsistencialDetalle>(http.post(`/pacientes/${pacienteId}/vinculos`, datos)),
  cerrarVinculo: (id: string, motivo: string) =>
    dato<VinculoAsistencialDetalle>(http.post(`/vinculos/${id}/fin`, { motivo })),
  credenciales: (usuarioId: string) =>
    dato<CredencialDetalle[]>(http.get(`/usuarios/${usuarioId}/credenciales`)),
  declararCredencial: (datos: z.input<typeof esquemaCredencial>) =>
    dato<CredencialDetalle>(http.post('/usuarios/yo/credenciales', datos)),
  revisarCredencial: (id: string, estado: 'revisada' | 'rechazada', motivo: string) =>
    dato<CredencialDetalle>(http.post(`/credenciales/${id}/revision`, { estado, motivo })),
};

// --- Acceso excepcional de soporte (F5.6) ----------------------------------------

export const soporte = {
  accesos: () => dato<AccesoSoporteDetalle[]>(http.get('/soporte/accesos')),
  decidir: (id: string, decision: 'autorizar' | 'rechazar', motivo: string) =>
    dato<AccesoSoporteDetalle>(http.post(`/soporte/accesos/${id}/decision`, { decision, motivo })),
  revocar: (id: string, motivo: string) =>
    dato<AccesoSoporteDetalle>(http.post(`/soporte/accesos/${id}/revocacion`, { motivo })),
};
